import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { useToast } from '../../hooks/use-toast';
import { Mail, Globe, Wallet, Eye, EyeOff } from 'lucide-react';
import Logo from '../../components/Logo';

const Login = () => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();
  const { toast } = useToast();

  const handleLogin = async (e) => {
    e.preventDefault();
    setLoading(true);
    
    try {
      const { authAPI } = await import('../../services/api');
      const response = await authAPI.login({ email, password });
      
      // Store authentication data
      const token = response.data.token;
      const user = response.data.user;
      
      localStorage.setItem('token', token);
      localStorage.setItem('user', JSON.stringify(user));
      
      // Verify token was saved
      const savedToken = localStorage.getItem('token');
      if (!savedToken) {
        console.error('Token was not saved to localStorage');
        toast({ title: 'Error', description: 'Failed to save login session. Please try again.', variant: 'destructive' });
        return;
      }
      
      // Dispatch custom event to notify BalanceContext of token change
      window.dispatchEvent(new Event('tokenChanged'));
      
      toast({ title: 'Login successful!', description: 'Welcome to Edge Trade' });
      
      // Small delay to ensure state updates before navigation
      await new Promise(resolve => setTimeout(resolve, 100));
      
      navigate('/home');
    } catch (error) {
      let message = 'Login failed. Please try again.';
      if (error.response?.data?.detail) {
        message = error.response.data.detail;
      } else if (error.response?.status === 503) {
        message = 'Service temporarily unavailable. Please try again in a moment.';
      } else if (!error.response) {
        message = 'Unable to connect to server. Please check your internet connection.';
      }
      toast({ title: 'Error', description: message, variant: 'destructive' });
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 via-blue-900 to-slate-900 flex flex-col">
      {/* Header */}
      <div className="flex items-center justify-between px-4 py-4 border-b border-cyan-500/20">
        <Logo size="small" showText={true} />
        <div className="flex gap-2">
          <Button variant="ghost" size="icon" className="rounded-full bg-cyan-500/20 hover:bg-cyan-500/30 neon-icon">
            <Mail className="w-5 h-5 text-cyan-400" />
          </Button>
          <Button variant="ghost" size="icon" className="rounded-full bg-cyan-500/20 hover:bg-cyan-500/30 neon-icon">
            <Globe className="w-5 h-5 text-cyan-400" />
          </Button>
        </div>
      </div>

      {/* Main Content */}
      <div className="flex-1 flex flex-col items-center justify-center px-6 py-8">
        <div className="w-full max-w-md">
          {/* Logo */}
          <div className="text-center mb-8">
            <div className="flex justify-center mb-4">
              <Logo size="large" showText={true} />
            </div>
            <h1 className="text-3xl font-black mb-2 text-cyan-400 neon-text">WELCOME TO EDGE TRADE</h1>
            <p className="text-cyan-600 text-sm font-bold">OPEN TRANSACTIONS ANYTIME, ANYWHERE SAFELY</p>
          </div>

          {/* Login Form */}
          <form onSubmit={handleLogin} className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="email" className="text-cyan-400">Email or Username</Label>
              <Input
                id="email"
                type="text"
                placeholder="Enter your email or username"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
                className="h-12 bg-slate-800/50 border-cyan-500/30 text-cyan-100 placeholder:text-cyan-700 focus:border-cyan-500 focus:ring-cyan-500/50"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="password" className="text-cyan-400">Password</Label>
              <div className="relative">
                <Input
                  id="password"
                  type={showPassword ? "text" : "password"}
                  placeholder="Enter your password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  required
                  className="h-12 bg-slate-800/50 border-cyan-500/30 text-cyan-100 placeholder:text-cyan-700 focus:border-cyan-500 focus:ring-cyan-500/50 pr-12"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-cyan-400 hover:text-cyan-300 transition-colors"
                >
                  {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                </button>
              </div>
            </div>
            <Button
              type="submit"
              className="w-full h-12 bg-gradient-to-r from-cyan-500 to-blue-600 hover:from-cyan-400 hover:to-blue-500 text-white font-semibold rounded-full neon-button"
              disabled={loading}
            >
              {loading ? 'Logging in...' : 'Log In'}
            </Button>
          </form>

          {/* Google Sign-in */}
          <div className="mt-4">
            <div className="flex items-center gap-3 mb-3">
              <div className="flex-1 h-px bg-cyan-500/20" />
              <span className="text-cyan-600 text-xs uppercase tracking-widest">or</span>
              <div className="flex-1 h-px bg-cyan-500/20" />
            </div>
            <button
              type="button"
              onClick={() => {
                // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR
                // REDIRECT URLS, THIS BREAKS THE AUTH.
                const redirectUrl = window.location.origin + '/auth/callback';
                window.location.href =
                  'https://auth.emergentagent.com/?redirect=' +
                  encodeURIComponent(redirectUrl);
              }}
              data-testid="google-signin-btn"
              className="w-full h-12 flex items-center justify-center gap-3 bg-white hover:bg-slate-100 text-slate-900 font-semibold rounded-full transition-all shadow-md"
            >
              <svg className="w-5 h-5" viewBox="0 0 24 24" aria-hidden="true">
                <path fill="#EA4335" d="M12 10.2v3.9h5.5c-.2 1.4-1.7 4.1-5.5 4.1-3.3 0-6-2.7-6-6.1s2.7-6.1 6-6.1c1.9 0 3.1.8 3.9 1.5l2.6-2.5C16.9 3.4 14.7 2.4 12 2.4 6.7 2.4 2.4 6.7 2.4 12S6.7 21.6 12 21.6c6.9 0 11.4-4.8 11.4-11.6 0-.8-.1-1.4-.2-2H12z"/>
              </svg>
              Sign in with Google
            </button>
          </div>

          {/* Register Link */}
          <div className="text-center mt-6">
            <button
              onClick={() => {
                const email = prompt('Enter your email address to reset your password:');
                if (email) {
                  fetch(`/api/auth/forgot-password`, {
                    method: 'POST',
                    headers: { 'Content-Type': 'application/json' },
                    body: JSON.stringify({ email })
                  })
                  .then(res => res.json())
                  .then(data => {
                    toast({ title: 'Password Reset', description: data.message });
                  })
                  .catch(() => {
                    toast({ title: 'Error', description: 'Failed to send reset email. Please try again.', variant: 'destructive' });
                  });
                }
              }}
              className="text-cyan-500 text-sm hover:text-cyan-400 transition-colors mb-4 block w-full"
            >
              Forgot Password?
            </button>
            <span className="text-cyan-600 text-sm">Don't have an account? </span>
            <button
              onClick={() => navigate('/register')}
              className="text-cyan-400 font-semibold text-sm hover:text-cyan-300 transition-colors"
            >
              Register Now
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Login;